import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ThingFlag } from '@fs/zion-icon'
import { usePageView } from '@fs/zion-analytics'
import { dmcRed, redFlagStyle } from '../../util/CssUtil'
import RelationshipChanges from './changes/RelationshipChanges'
import RelationshipSummary from './summary/RelationshipSummary'
import SubnavAndRoutes from '../app/SubnavAndRoutes'
import { useRelationshipState } from './useRelationshipState'
import { RawData } from '../common/RawData'
import { RelationshipContextProvider } from './relationshipUtils'
import RelationshipRawData from './RelationshipRawData'
import useRelationshipChangesState from './useRelationshipChangesState'
import { getRelationshipChanges } from '../../services/RelationshipService'
import useNotifications from '../../hooks/useNotifications'
import { usePageTitle } from '../app/PageTitleContext'

export default function RelationshipBase({ match }) {
  usePageView({ page_detail: 'DMConsole:RelationshipBase' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.relationship.base'))
  const { relationshipId, relationshipType } = match.params
  const { relationshipState, relationshipActions } = useRelationshipState(relationshipId, relationshipType)
  const { state, dispatch } = useRelationshipChangesState(relationshipId, relationshipType)
  const { error } = useNotifications()

  useEffect(() => {
    relationshipActions.fetchCurrentRelationship()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationshipId, relationshipType])

  useEffect(() => {
    const fetchData = async () => {
      if (state?.status === 'loadingStatus' && state?.relationshipId) {
        try {
          const response = await getRelationshipChanges(
            state.relationshipType,
            state.relationshipId,
            state.nextPageToken
          )
          if (response.status === 200) {
            if (!response.data.hasMoreChanges) dispatch({ type: 'reachedEndAction', payload: response.data })
            dispatch({ type: 'fetchSuccessAction', payload: response.data })
          }
          dispatch({ type: 'fetchErrorAction' })
        } catch (err) {
          error(t('relationship.fetch.error', `Error fetching relationship: ${err.message || ''}`), err)
          dispatch({ type: 'fetchErrorAction' })
        }
      }
    }
    fetchData()
  }, [state, dispatch, t, error])

  const subNavs = [
    {
      text: t('relationships.summary.label', 'Summary'),
      path: 'summary',
      requiredPermissions: 'FtLoginPermission',
      component: RelationshipSummary,
    },
    {
      text:
        state?.changes?.length > 0 ? (
          <span>
            <span style={redFlagStyle}>{t('relationships.changes.label', 'Changes')}</span>
            <ThingFlag size="xs" color={dmcRed} />
          </span>
        ) : (
          t('relationships.changes.label', 'Changes')
        ),
      path: 'changes',
      requiredPermissions: 'FtLoginPermission',
      component: RelationshipChanges,
    },
    {
      text: t('relationships.rawData.label', 'Raw Data'),
      path: 'rawdata',
      requiredPermissions: 'FtLoginPermission',
      component: RelationshipRawData,
    },
  ]

  if (relationshipState.relationshipData === null) {
    return <>{t('relationships.lookupRelationship', 'Look up a Relationship to view Relationship data')}</>
  }
  if (relationshipState.relationshipData === undefined) {
    return null
  }

  return (
    <RelationshipContextProvider value={{ relationshipState, relationshipActions, state, dispatch }}>
      <SubnavAndRoutes data={subNavs} />
      <RawData state={relationshipState} />
    </RelationshipContextProvider>
  )
}
